import { inject, Injectable } from '@angular/core';
import {
  assertServiceOk,
  AwsApiWrapperService,
  GenericResponseResult,
} from '@brightside-web/desktop/data-access/core-services';
import { Environment } from '@micro-core/environment';
import { from, Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  CLIENT_PREFERENCE_KEYS,
  type ClientPreferenceItem,
  type ClientPreferencePatchItem,
  type ClientPreferencesPayload,
} from '../models/client-preferences.models';

export const DEV_SMS_PREFERENCES_MODE_SESSION_KEY = 'brightside_dev_sms_preferences_mode';

/** Dev-menu override for SMS preferences API behavior (non-prod only). */
export type SmsPreferencesDevMode = 'off' | 'force-prompt' | 'get-fail' | 'patch-fail';

const MOCK_DEV_PREFERENCES: ClientPreferenceItem[] = [
  {
    key: CLIENT_PREFERENCE_KEYS.smsMarketingConsent,
    promptForValue: true,
    booleanValue: false,
  },
  {
    key: CLIENT_PREFERENCE_KEYS.emailMarketingConsent,
    promptForValue: false,
    booleanValue: false,
  },
];

@Injectable({
  providedIn: 'root',
})
export class ClientPreferencesService {
  private readonly _awsApiWrapper = inject(AwsApiWrapperService);
  private readonly _env = inject(Environment);

  getPreferences(): Observable<ClientPreferenceItem[]> {
    const devMode = this._readDevMode();
    if (!this._env.production && devMode === 'get-fail') {
      return throwError(() => new Error('dev: preferences GET failed'));
    }
    if (!this._env.production && (devMode === 'force-prompt' || devMode === 'patch-fail')) {
      return of([...MOCK_DEV_PREFERENCES]);
    }
    return from(
      this._awsApiWrapper.get('api-mobile', '/client/preferences', { body: null }),
    ).pipe(map((res) => this._mapPreferencesResponse(res)));
  }

  updatePreferences(updates: ClientPreferencePatchItem[]): Observable<ClientPreferenceItem[]> {
    const devMode = this._readDevMode();
    if (!this._env.production && devMode === 'patch-fail') {
      return throwError(() => new Error('dev: preferences PATCH failed'));
    }
    if (!this._env.production && devMode === 'force-prompt') {
      return of(
        updates.map((update) => ({
          key: update.key,
          promptForValue:
            update.key === CLIENT_PREFERENCE_KEYS.smsMarketingConsent ? false : undefined,
          booleanValue: update.booleanValue ?? false,
        })),
      );
    }
    return from(
      this._awsApiWrapper.patch('api-mobile', '/client/preferences', { body: updates }),
    ).pipe(map((res) => this._mapPreferencesResponse(res)));
  }

  findPreference(items: ClientPreferenceItem[], key: string): ClientPreferenceItem | undefined {
    return items.find((item) => item.key === key);
  }

  getBooleanValue(items: ClientPreferenceItem[], key: string): boolean | undefined {
    return this.findPreference(items, key)?.booleanValue;
  }

  shouldPromptForSmsMarketing(items: ClientPreferenceItem[]): boolean {
    const sms = this.findPreference(items, CLIENT_PREFERENCE_KEYS.smsMarketingConsent);
    return sms?.promptForValue === true;
  }

  private _mapPreferencesResponse(
    res: ClientPreferencesPayload & GenericResponseResult,
  ): ClientPreferenceItem[] {
    assertServiceOk(res);
    return res.payload ?? [];
  }

  private _readDevMode(): SmsPreferencesDevMode {
    if (this._env.production || typeof sessionStorage === 'undefined') {
      return 'off';
    }
    const raw = sessionStorage.getItem(DEV_SMS_PREFERENCES_MODE_SESSION_KEY);
    if (raw === 'force-prompt' || raw === 'get-fail' || raw === 'patch-fail') {
      return raw;
    }
    return 'off';
  }
}
